import type {
  OpensearchBooleanQuery,
  PathAwareQueryContainer,
} from "@redotech/opensearch-types/types";
import { SPAM_TAG } from "@redotech/redo-model/conversation-tags/conversation-tag-record";
import {
  type ArrayStringFilterData,
  ArrayStringFilterOperator,
} from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import { DateFilterOperator } from "@redotech/redo-model/views/advanced-filters/date-filter";
import { ExistsFilterOperator } from "@redotech/redo-model/views/advanced-filters/exists-filter";
import {
  AdvancedFilterType,
  decodeFilterGroup,
  encodeFilterGroup,
  FilterOperator,
  isFilterGroup,
} from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import { NumberFilterOperator } from "@redotech/redo-model/views/advanced-filters/number-filter";
import { getFilterBuilder } from "@redotech/redo-model/views/utils/util";
import type {
  AdvancedFilterData,
  FilterGroup,
} from "@redotech/redo-model/views/views";
import { AccountId } from "../ids/definitions";
import {
  isSupportTicketStatus,
  type SupportTicketSearchHighlightField,
  type SupportTicketStatus,
} from "../models/shopper-ai/conversation/support-ticket";

export const UNASSIGNED_ASSIGNEE_SENTINEL = "UNASSIGNED";

/** Source of truth for support ticket filter names. */
export const supportTicketFilterNames = [
  "status",
  "assignees",
  "assigneeType",
  "channels",
  "supportTicketTags",
  "customerTags",
  "mentions",
  "readAt",
  "words",
  "createdAt",
  "closedAt",
  "lastResponseAt",
  "lastCustomerMessageAt",
  "lastMerchantMessageAt",
  "customerName",
  "participantKeys",
  "primaryParticipantKeys",
  "shopperParticipantIds",
  "merchantUserParticipantIds",
  "redoUserParticipantIds",
  "agentParticipantIds",
  "systemParticipantKeys",
  "numDrafts",
  "numSentMessages",
  "numOutboundMessages",
  "numCustomerMessages",
  "numMerchantSentMessages",
  "numAiSentMessages",
  "numAutoReplySentMessages",
  "numSystemSentMessages",
  "numInternalNotes",
] as const;

/** Valid support ticket filter name. */
export type SupportTicketFilterName = (typeof supportTicketFilterNames)[number];

/** SCREAMING_CASE helpers for callers that prefer enum-style references. */
export const SupportTicketFilterNames = {
  STATUS: "status",
  ASSIGNEES: "assignees",
  ASSIGNEE_TYPE: "assigneeType",
  CHANNELS: "channels",
  SUPPORT_TICKET_TAGS: "supportTicketTags",
  CUSTOMER_TAGS: "customerTags",
  MENTIONS: "mentions",
  READ_AT: "readAt",
  WORDS: "words",
  CREATED_AT: "createdAt",
  CLOSED_AT: "closedAt",
  LAST_RESPONSE_AT: "lastResponseAt",
  LAST_CUSTOMER_MESSAGE_AT: "lastCustomerMessageAt",
  LAST_MERCHANT_MESSAGE_AT: "lastMerchantMessageAt",
  CUSTOMER_NAME: "customerName",
  PARTICIPANT_KEYS: "participantKeys",
  PRIMARY_PARTICIPANT_KEYS: "primaryParticipantKeys",
  SHOPPER_PARTICIPANT_IDS: "shopperParticipantIds",
  MERCHANT_USER_PARTICIPANT_IDS: "merchantUserParticipantIds",
  REDO_USER_PARTICIPANT_IDS: "redoUserParticipantIds",
  AGENT_PARTICIPANT_IDS: "agentParticipantIds",
  SYSTEM_PARTICIPANT_KEYS: "systemParticipantKeys",
  NUM_DRAFTS: "numDrafts",
  NUM_SENT_MESSAGES: "numSentMessages",
  NUM_OUTBOUND_MESSAGES: "numOutboundMessages",
  NUM_CUSTOMER_MESSAGES: "numCustomerMessages",
  NUM_MERCHANT_SENT_MESSAGES: "numMerchantSentMessages",
  NUM_AI_SENT_MESSAGES: "numAiSentMessages",
  NUM_AUTO_REPLY_SENT_MESSAGES: "numAutoReplySentMessages",
  NUM_SYSTEM_SENT_MESSAGES: "numSystemSentMessages",
  NUM_INTERNAL_NOTES: "numInternalNotes",
} as const satisfies Record<string, SupportTicketFilterName>;
export type SupportTicketFilterNames =
  (typeof SupportTicketFilterNames)[keyof typeof SupportTicketFilterNames];

const supportTicketFilterNameAliases: Partial<
  Record<string, SupportTicketFilterName>
> = { conversationTags: SupportTicketFilterNames.SUPPORT_TICKET_TAGS };

export function resolveSupportTicketFilterName(
  name: string,
): SupportTicketFilterName | null {
  const filterName = supportTicketFilterNames.find((n) => n === name);
  return filterName ?? supportTicketFilterNameAliases[name] ?? null;
}

/** Default filter data for each support ticket filter. */
export const supportTicketFilterData = {
  [SupportTicketFilterNames.STATUS]: {
    name: SupportTicketFilterNames.STATUS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.ASSIGNEES]: {
    name: SupportTicketFilterNames.ASSIGNEES,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.ASSIGNEE_TYPE]: {
    name: SupportTicketFilterNames.ASSIGNEE_TYPE,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.CHANNELS]: {
    name: SupportTicketFilterNames.CHANNELS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.SUPPORT_TICKET_TAGS]: {
    name: SupportTicketFilterNames.SUPPORT_TICKET_TAGS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.CUSTOMER_TAGS]: {
    name: SupportTicketFilterNames.CUSTOMER_TAGS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.MENTIONS]: {
    name: SupportTicketFilterNames.MENTIONS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.READ_AT]: {
    name: SupportTicketFilterNames.READ_AT,
    type: AdvancedFilterType.EXISTS,
    value: null,
    operator: ExistsFilterOperator.NOT_EXISTS,
  },
  [SupportTicketFilterNames.WORDS]: {
    name: SupportTicketFilterNames.WORDS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.CONTAINS,
  },
  [SupportTicketFilterNames.CREATED_AT]: {
    name: SupportTicketFilterNames.CREATED_AT,
    type: AdvancedFilterType.DATE,
    value: null,
    operator: DateFilterOperator.WITHIN,
  },
  [SupportTicketFilterNames.CLOSED_AT]: {
    name: SupportTicketFilterNames.CLOSED_AT,
    type: AdvancedFilterType.DATE,
    value: null,
    operator: DateFilterOperator.WITHIN,
  },
  [SupportTicketFilterNames.LAST_RESPONSE_AT]: {
    name: SupportTicketFilterNames.LAST_RESPONSE_AT,
    type: AdvancedFilterType.DATE,
    value: null,
    operator: DateFilterOperator.WITHIN,
  },
  [SupportTicketFilterNames.LAST_CUSTOMER_MESSAGE_AT]: {
    name: SupportTicketFilterNames.LAST_CUSTOMER_MESSAGE_AT,
    type: AdvancedFilterType.DATE,
    value: null,
    operator: DateFilterOperator.WITHIN,
  },
  [SupportTicketFilterNames.LAST_MERCHANT_MESSAGE_AT]: {
    name: SupportTicketFilterNames.LAST_MERCHANT_MESSAGE_AT,
    type: AdvancedFilterType.DATE,
    value: null,
    operator: DateFilterOperator.WITHIN,
  },
  [SupportTicketFilterNames.CUSTOMER_NAME]: {
    name: SupportTicketFilterNames.CUSTOMER_NAME,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.CONTAINS,
  },
  [SupportTicketFilterNames.PARTICIPANT_KEYS]: {
    name: SupportTicketFilterNames.PARTICIPANT_KEYS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.PRIMARY_PARTICIPANT_KEYS]: {
    name: SupportTicketFilterNames.PRIMARY_PARTICIPANT_KEYS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.SHOPPER_PARTICIPANT_IDS]: {
    name: SupportTicketFilterNames.SHOPPER_PARTICIPANT_IDS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.MERCHANT_USER_PARTICIPANT_IDS]: {
    name: SupportTicketFilterNames.MERCHANT_USER_PARTICIPANT_IDS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.REDO_USER_PARTICIPANT_IDS]: {
    name: SupportTicketFilterNames.REDO_USER_PARTICIPANT_IDS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.AGENT_PARTICIPANT_IDS]: {
    name: SupportTicketFilterNames.AGENT_PARTICIPANT_IDS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.SYSTEM_PARTICIPANT_KEYS]: {
    name: SupportTicketFilterNames.SYSTEM_PARTICIPANT_KEYS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.NUM_DRAFTS]: {
    name: SupportTicketFilterNames.NUM_DRAFTS,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.NUM_SENT_MESSAGES]: {
    name: SupportTicketFilterNames.NUM_SENT_MESSAGES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.NUM_OUTBOUND_MESSAGES]: {
    name: SupportTicketFilterNames.NUM_OUTBOUND_MESSAGES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.NUM_CUSTOMER_MESSAGES]: {
    name: SupportTicketFilterNames.NUM_CUSTOMER_MESSAGES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.NUM_MERCHANT_SENT_MESSAGES]: {
    name: SupportTicketFilterNames.NUM_MERCHANT_SENT_MESSAGES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.NUM_AI_SENT_MESSAGES]: {
    name: SupportTicketFilterNames.NUM_AI_SENT_MESSAGES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.NUM_AUTO_REPLY_SENT_MESSAGES]: {
    name: SupportTicketFilterNames.NUM_AUTO_REPLY_SENT_MESSAGES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.NUM_SYSTEM_SENT_MESSAGES]: {
    name: SupportTicketFilterNames.NUM_SYSTEM_SENT_MESSAGES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.NUM_INTERNAL_NOTES]: {
    name: SupportTicketFilterNames.NUM_INTERNAL_NOTES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
} satisfies Record<SupportTicketFilterName, AdvancedFilterData>;

interface SupportTicketFilterPathShape {
  opensearchPath: string;
  keywordPath?: string;
}

export type SupportTicketClientEvaluation = "client" | "server";

/** Filter name to OpenSearch path mapping. */
const supportTicketFilterPathRecord = {
  status: { opensearchPath: "status", keywordPath: "status" },
  assignees: { opensearchPath: "assigneeId", keywordPath: "assigneeId" },
  assigneeType: { opensearchPath: "assigneeType", keywordPath: "assigneeType" },
  channels: { opensearchPath: "channels", keywordPath: "channels" },
  supportTicketTags: { opensearchPath: "tags.id", keywordPath: "tags.id" },
  customerTags: {
    opensearchPath: "customerTags",
    keywordPath: "customerTags.keyword",
  },
  mentions: { opensearchPath: "mentions", keywordPath: "mentions" },
  readAt: { opensearchPath: "readAt" },
  words: { opensearchPath: "summary", keywordPath: "summary.keyword" },
  createdAt: { opensearchPath: "createdAt" },
  closedAt: { opensearchPath: "closedAt" },
  lastResponseAt: { opensearchPath: "lastResponseAt" },
  lastCustomerMessageAt: { opensearchPath: "lastCustomerMessageAt" },
  lastMerchantMessageAt: { opensearchPath: "lastMerchantMessageAt" },
  customerName: {
    opensearchPath: "customerName",
    keywordPath: "customerName.keyword",
  },
  participantKeys: {
    opensearchPath: "participantKeys",
    keywordPath: "participantKeys",
  },
  primaryParticipantKeys: {
    opensearchPath: "primaryParticipantKeys",
    keywordPath: "primaryParticipantKeys",
  },
  shopperParticipantIds: {
    opensearchPath: "shopperParticipantIds",
    keywordPath: "shopperParticipantIds",
  },
  merchantUserParticipantIds: {
    opensearchPath: "merchantUserParticipantIds",
    keywordPath: "merchantUserParticipantIds",
  },
  redoUserParticipantIds: {
    opensearchPath: "redoUserParticipantIds",
    keywordPath: "redoUserParticipantIds",
  },
  agentParticipantIds: {
    opensearchPath: "agentParticipantIds",
    keywordPath: "agentParticipantIds",
  },
  systemParticipantKeys: {
    opensearchPath: "systemParticipantKeys",
    keywordPath: "systemParticipantKeys",
  },
  numDrafts: { opensearchPath: "numDrafts" },
  numSentMessages: { opensearchPath: "numSentMessages" },
  numOutboundMessages: { opensearchPath: "numOutboundMessages" },
  numCustomerMessages: { opensearchPath: "numCustomerMessages" },
  numMerchantSentMessages: { opensearchPath: "numMerchantSentMessages" },
  numAiSentMessages: { opensearchPath: "numAiSentMessages" },
  numAutoReplySentMessages: { opensearchPath: "numAutoReplySentMessages" },
  numSystemSentMessages: { opensearchPath: "numSystemSentMessages" },
  numInternalNotes: { opensearchPath: "numInternalNotes" },
} as const satisfies Record<
  SupportTicketFilterName,
  SupportTicketFilterPathShape
>;

type SupportTicketFilterPathValue =
  (typeof supportTicketFilterPathRecord)[SupportTicketFilterName];
type SupportTicketFilterKeywordPath<T> = T extends {
  readonly keywordPath: infer Path extends string;
}
  ? Path
  : never;
type SupportTicketMappedOpensearchPath =
  | SupportTicketFilterPathValue["opensearchPath"]
  | SupportTicketFilterKeywordPath<SupportTicketFilterPathValue>;
type SupportTicketDefaultOpensearchPath =
  | SupportTicketSearchHighlightField
  | "accountId"
  | "deletedAt"
  | "kind"
  | "mergedSourceTicketNumbers"
  | "snoozedUntil"
  | "tags"
  | "tags.name.keyword"
  | "ticketNumber";
export type SupportTicketOpensearchPath =
  | SupportTicketMappedOpensearchPath
  | SupportTicketDefaultOpensearchPath;
export type SupportTicketOpensearchQuery =
  PathAwareQueryContainer<SupportTicketOpensearchPath>;
export type SupportTicketOpensearchBooleanQuery =
  OpensearchBooleanQuery<SupportTicketOpensearchPath>;

/** OpenSearch path mapping for a support ticket filter. */
export interface SupportTicketFilterPath {
  opensearchPath: SupportTicketOpensearchPath;
  keywordPath?: SupportTicketOpensearchPath;
}

/** Gets the OpenSearch path mapping for a support ticket filter. */
export function getSupportTicketFilterPath(
  name: SupportTicketFilterName,
): SupportTicketFilterPath {
  return supportTicketFilterPathRecord[name];
}

const supportTicketClientEvaluationRecord: Record<
  SupportTicketFilterName,
  SupportTicketClientEvaluation
> = {
  status: "client",
  assignees: "client",
  assigneeType: "server",
  channels: "client",
  supportTicketTags: "client",
  customerTags: "server",
  mentions: "server",
  readAt: "client",
  words: "server",
  createdAt: "server",
  closedAt: "server",
  lastResponseAt: "server",
  lastCustomerMessageAt: "server",
  lastMerchantMessageAt: "server",
  customerName: "server",
  participantKeys: "server",
  primaryParticipantKeys: "server",
  shopperParticipantIds: "server",
  merchantUserParticipantIds: "server",
  redoUserParticipantIds: "server",
  agentParticipantIds: "server",
  systemParticipantKeys: "server",
  numDrafts: "client",
  numSentMessages: "client",
  numOutboundMessages: "client",
  numCustomerMessages: "client",
  numMerchantSentMessages: "client",
  numAiSentMessages: "client",
  numAutoReplySentMessages: "client",
  numSystemSentMessages: "client",
  numInternalNotes: "client",
};

export function getSupportTicketClientEvaluation(
  name: string,
): SupportTicketClientEvaluation | null {
  const filterName = resolveSupportTicketFilterName(name);
  if (!filterName) {
    return null;
  }
  return supportTicketClientEvaluationRecord[filterName];
}

/** Gets default filter data, or undefined for an unknown filter name. */
export function getSupportTicketFilterData(
  name: string,
): AdvancedFilterData | undefined {
  const filterName = resolveSupportTicketFilterName(name);
  if (!filterName) {
    return undefined;
  }
  return supportTicketFilterData[filterName];
}

/**
 * Encodes support-ticket filters for URLs and support-ticket RPC requests.
 */
export function encodeSupportTicketFilterGroup(group: FilterGroup): string {
  return encodeFilterGroup(group, (filter) => {
    const filterBuilder = getFilterBuilder(filter);
    return `${filter.name}@@${filterBuilder.writeToString(filter)}`;
  });
}

/**
 * Decodes support-ticket filters from URLs and support-ticket RPC requests.
 */
export function decodeSupportTicketFilterGroup(
  encoded: string | null | undefined,
): FilterGroup | null {
  if (!encoded) {
    return null;
  }

  return decodeFilterGroup<AdvancedFilterData>(encoded, (filterStr) => {
    const [rawName, dataString] = filterStr.split("@@");
    if (!rawName || dataString === undefined) {
      return null;
    }

    const filterName = resolveSupportTicketFilterName(rawName);
    if (!filterName) {
      return null;
    }

    const defaultFilter = supportTicketFilterData[filterName];
    const filterBuilder = getFilterBuilder(defaultFilter);
    return (
      filterBuilder.readFromString(filterName, dataString, defaultFilter) ??
      null
    );
  });
}

export function getSupportTicketLeafFilters(
  group: FilterGroup,
): AdvancedFilterData[] {
  const filters: AdvancedFilterData[] = [];
  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      filters.push(...getSupportTicketLeafFilters(clause));
    } else {
      filters.push(clause);
    }
  }
  return filters;
}

export interface SupportTicketFilterGroupWalker<TContext> {
  createChildContext(parent: TContext, operator: FilterOperator): TContext;
  visitFilter(filter: AdvancedFilterData, context: TContext): void;
  visitGroup(context: TContext, group: FilterGroup): void;
  appendChildContext(parent: TContext, child: TContext): void;
}

export function walkSupportTicketFilterGroup<TContext>(
  group: FilterGroup,
  context: TContext,
  walker: SupportTicketFilterGroupWalker<TContext>,
): void {
  walker.visitGroup(context, group);
  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      const childContext = walker.createChildContext(context, clause.operator);
      walkSupportTicketFilterGroup(clause, childContext, walker);
      walker.appendChildContext(context, childContext);
    } else {
      walker.visitFilter(clause, context);
    }
  }
}

function createEmptyBoolQuery(): SupportTicketOpensearchBooleanQuery {
  return { filter: [], must: [], must_not: [], should: [] };
}

const DEFAULT_EXCLUDED_SUPPORT_TICKET_STATUSES = [
  "HIDDEN",
  "ARCHIVED",
  "DELETED",
] as const satisfies readonly SupportTicketStatus[];

function addLifecycleVisibilityExclusions(
  compound: SupportTicketOpensearchBooleanQuery,
  options: {
    includeDeletedAtExclusion: boolean;
    statusExclusions: readonly SupportTicketStatus[];
  },
): void {
  if (options.includeDeletedAtExclusion) {
    compound.must_not.push({ exists: { field: "deletedAt" } });
  }
  if (options.statusExclusions.length > 0) {
    compound.must_not.push({
      terms: { status: [...options.statusExclusions] },
    });
  }
}

function addDefaultSupportTicketExclusions(
  compound: SupportTicketOpensearchBooleanQuery,
  options: {
    includeDeletedAtExclusion: boolean;
    includeSpamExclusion: boolean;
    includeAiAssigneeExclusion: boolean;
    statusExclusions: readonly SupportTicketStatus[];
  },
): void {
  addLifecycleVisibilityExclusions(compound, options);
  if (options.includeAiAssigneeExclusion) {
    compound.must_not.push({
      bool: {
        filter: [
          { term: { assigneeType: "AI" } },
          { term: { channels: "redoChat" } },
        ],
      },
    });
  }
  compound.must_not.push({
    bool: {
      filter: [
        { term: { status: "OPEN" } },
        { term: { assigneeType: "CHAT_FLOW" } },
      ],
    },
  });
  if (options.includeSpamExclusion) {
    // SPAM_TAG is the tag name "spam", not an ID, so query tags.name.keyword
    compound.must_not.push({
      nested: {
        path: "tags",
        query: { term: { "tags.name.keyword": SPAM_TAG } },
      },
    });
  }
}

function isArrayStringFilter(
  filter: AdvancedFilterData,
): filter is ArrayStringFilterData {
  return filter.type === AdvancedFilterType.ARRAY_TO_STRING;
}

/**
 * Returns true when a leaf filter references the spam tag in any operator
 * (ANY_OF / ALL_OF / NONE_OF). Callers use this to skip the default spam
 * exclusion so views that explicitly target spam — most importantly the Spam
 * view — still return results.
 */
function filterReferencesSpamTag(filter: AdvancedFilterData): boolean {
  const resolvedName = resolveSupportTicketFilterName(filter.name);
  if (resolvedName !== SupportTicketFilterNames.SUPPORT_TICKET_TAGS) {
    return false;
  }
  if (!isArrayStringFilter(filter)) {
    return false;
  }
  return (filter.value ?? []).includes(SPAM_TAG);
}

function filterGroupReferencesSpamTag(group: FilterGroup): boolean {
  return getSupportTicketLeafFilters(group).some(filterReferencesSpamTag);
}

/**
 * Returns true when a leaf filter explicitly opts in to AI-assigned tickets via
 * `assigneeType ANY_OF ["AI"]`. Used to suppress the default AI-assignee
 * exclusion for views that intentionally surface AI-assigned tickets.
 */
function filterReferencesAiAssigneeType(filter: AdvancedFilterData): boolean {
  const resolvedName = resolveSupportTicketFilterName(filter.name);
  if (resolvedName !== SupportTicketFilterNames.ASSIGNEE_TYPE) {
    return false;
  }
  if (!isArrayStringFilter(filter)) {
    return false;
  }
  if (filter.operator !== ArrayStringFilterOperator.ANY_OF) {
    return false;
  }
  return (filter.value ?? []).includes("AI");
}

function filterGroupReferencesAiAssigneeType(group: FilterGroup): boolean {
  return getSupportTicketLeafFilters(group).some(
    filterReferencesAiAssigneeType,
  );
}

function getFilterExplicitlyIncludedStatuses(
  filter: AdvancedFilterData,
): Set<SupportTicketStatus> {
  const statuses = new Set<SupportTicketStatus>();
  const resolvedName = resolveSupportTicketFilterName(filter.name);
  if (resolvedName !== SupportTicketFilterNames.STATUS) {
    return statuses;
  }
  if (!isArrayStringFilter(filter)) {
    return statuses;
  }
  if (
    filter.operator !== ArrayStringFilterOperator.ANY_OF &&
    filter.operator !== ArrayStringFilterOperator.ALL_OF
  ) {
    return statuses;
  }
  for (const status of filter.value ?? []) {
    if (isSupportTicketStatus(status)) {
      statuses.add(status);
    }
  }
  return statuses;
}

function addStatuses(
  target: Set<SupportTicketStatus>,
  source: ReadonlySet<SupportTicketStatus>,
): void {
  for (const status of source) {
    target.add(status);
  }
}

function combineStatuses(
  left: ReadonlySet<SupportTicketStatus>,
  right: ReadonlySet<SupportTicketStatus>,
): Set<SupportTicketStatus> {
  const combined = new Set(left);
  addStatuses(combined, right);
  return combined;
}

function getExplicitlyIncludedStatuses(
  filters: readonly AdvancedFilterData[],
): Set<SupportTicketStatus> {
  const statuses = new Set<SupportTicketStatus>();
  for (const filter of filters) {
    addStatuses(statuses, getFilterExplicitlyIncludedStatuses(filter));
  }
  return statuses;
}

function getDefaultExclusionOptions(filters: readonly AdvancedFilterData[]): {
  includeDeletedAtExclusion: boolean;
  statusExclusions: readonly SupportTicketStatus[];
} {
  const explicitlyIncludedStatuses = getExplicitlyIncludedStatuses(filters);
  return {
    includeDeletedAtExclusion: !explicitlyIncludedStatuses.has("DELETED"),
    statusExclusions: DEFAULT_EXCLUDED_SUPPORT_TICKET_STATUSES.filter(
      (status) => !explicitlyIncludedStatuses.has(status),
    ),
  };
}

function getConjunctiveExplicitStatuses(
  group: FilterGroup,
): Set<SupportTicketStatus> {
  const statuses = new Set<SupportTicketStatus>();
  if (group.operator !== FilterOperator.AND) {
    return statuses;
  }
  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      addStatuses(statuses, getConjunctiveExplicitStatuses(clause));
    } else {
      addStatuses(statuses, getFilterExplicitlyIncludedStatuses(clause));
    }
  }
  return statuses;
}

interface ScopedLifecycleExclusionOptions {
  readonly includeDeletedAtExclusion: boolean;
  readonly statusExclusions: readonly SupportTicketStatus[];
}

function getScopedLifecycleExclusionOptions(
  topLevelOptions: ScopedLifecycleExclusionOptions,
): ScopedLifecycleExclusionOptions {
  return {
    includeDeletedAtExclusion: !topLevelOptions.includeDeletedAtExclusion,
    statusExclusions: DEFAULT_EXCLUDED_SUPPORT_TICKET_STATUSES.filter(
      (status) => !topLevelOptions.statusExclusions.includes(status),
    ),
  };
}

function addScopedLifecycleExclusions(
  compound: SupportTicketOpensearchBooleanQuery,
  scopedOptions: ScopedLifecycleExclusionOptions,
  explicitlyIncludedStatuses: ReadonlySet<SupportTicketStatus>,
): void {
  const statusExclusions = scopedOptions.statusExclusions.filter(
    (status) => !explicitlyIncludedStatuses.has(status),
  );
  const includeDeletedAtExclusion =
    scopedOptions.includeDeletedAtExclusion &&
    !explicitlyIncludedStatuses.has("DELETED");
  if (!includeDeletedAtExclusion && statusExclusions.length === 0) {
    return;
  }
  addLifecycleVisibilityExclusions(compound, {
    includeDeletedAtExclusion,
    statusExclusions,
  });
}

export const supportTicketWordsSearchFields = [
  "summary",
  "messageContent",
  "emailSubject",
  "customerName",
  "customerEmail",
  "customerPhone",
  "socialHandles",
] as const satisfies readonly SupportTicketSearchHighlightField[];
export type SupportTicketWordsSearchField =
  (typeof supportTicketWordsSearchFields)[number];

export const supportTicketCustomerSearchFields = [
  "customerName",
  "customerEmail",
  "customerPhone",
  "socialHandles",
] as const satisfies readonly SupportTicketSearchHighlightField[];

export interface SupportTicketSearchHighlightTerm {
  searchTerm: string;
  fields: readonly SupportTicketSearchHighlightField[];
}

function isIntegerString(value: string): boolean {
  return /^\d+$/.test(value);
}

export function getSupportTicketWordsSearchTerm(
  filter: AdvancedFilterData,
): string | null {
  if (!isArrayStringFilter(filter)) {
    return null;
  }

  const searchTerm = (filter.value?.[0] as string | undefined)
    ?.substring(0, 200)
    .trim();
  return searchTerm || null;
}

export function getSupportTicketPositiveWordsSearchTerm(
  filterGroup: FilterGroup | null | undefined,
): string | null {
  return (
    getSupportTicketSearchHighlightTerms(filterGroup).find((term) =>
      term.fields.includes("summary"),
    )?.searchTerm ?? null
  );
}

export function getSupportTicketSearchHighlightTerms(
  filterGroup: FilterGroup | null | undefined,
): SupportTicketSearchHighlightTerm[] {
  if (!filterGroup) {
    return [];
  }

  return getSupportTicketLeafFilters(filterGroup).flatMap(
    (filter): SupportTicketSearchHighlightTerm[] => {
      if (!isArrayStringFilter(filter)) {
        return [];
      }
      if (filter.operator === ArrayStringFilterOperator.NOT_CONTAINS) {
        return [];
      }

      const searchTerm = getSupportTicketWordsSearchTerm(filter);
      if (!searchTerm) {
        return [];
      }

      if (filter.name === SupportTicketFilterNames.WORDS) {
        return [{ searchTerm, fields: supportTicketWordsSearchFields }];
      }
      if (filter.name === SupportTicketFilterNames.CUSTOMER_NAME) {
        return [{ searchTerm, fields: supportTicketCustomerSearchFields }];
      }

      return [];
    },
  );
}

function addWordsFilter(
  filter: ArrayStringFilterData,
  compound: SupportTicketOpensearchBooleanQuery,
): void {
  const searchTerm = getSupportTicketWordsSearchTerm(filter);
  if (!searchTerm) {
    return;
  }

  const should: SupportTicketOpensearchQuery[] =
    supportTicketWordsSearchFields.map((f) => ({ match: { [f]: searchTerm } }));

  if (isIntegerString(searchTerm)) {
    should.push({ term: { ticketNumber: Number(searchTerm) } });
    should.push({ term: { mergedSourceTicketNumbers: Number(searchTerm) } });
  }

  const multiFieldQuery = {
    bool: { should, minimum_should_match: 1 },
  } satisfies SupportTicketOpensearchQuery;

  if (filter.operator === ArrayStringFilterOperator.NOT_CONTAINS) {
    compound.must_not.push(multiFieldQuery);
  } else {
    compound.filter.push(multiFieldQuery);
  }
}

function addCustomerSearchFilter(
  filter: ArrayStringFilterData,
  compound: SupportTicketOpensearchBooleanQuery,
): void {
  const searchTerm = getSupportTicketWordsSearchTerm(filter);
  if (!searchTerm) {
    return;
  }

  const multiFieldQuery = {
    bool: {
      should: supportTicketCustomerSearchFields.map((field) => ({
        match: { [field]: searchTerm },
      })),
      minimum_should_match: 1,
    },
  } satisfies SupportTicketOpensearchQuery;

  if (filter.operator === ArrayStringFilterOperator.NOT_CONTAINS) {
    compound.must_not.push(multiFieldQuery);
  } else {
    compound.filter.push(multiFieldQuery);
  }
}

function addSupportTicketTagsFilter(
  filter: ArrayStringFilterData,
  path: SupportTicketFilterPath,
  compound: SupportTicketOpensearchBooleanQuery,
): void {
  const tagValues = filter.value ?? [];
  if (tagValues.length === 0) {
    return;
  }

  // SPAM_TAG is a tag name ("spam"), not an ID. When filtering only on the
  // spam tag, query tags.name.keyword instead of tags.id.
  const isSpamTagOnly = tagValues.length === 1 && tagValues[0] === SPAM_TAG;
  const fieldPath = isSpamTagOnly ? "tags.name.keyword" : getKeywordPath(path);

  if (filter.operator === ArrayStringFilterOperator.ALL_OF) {
    // ALL_OF: Every specified tag must be present
    for (const value of tagValues) {
      compound.filter.push({
        nested: { path: "tags", query: { term: { [fieldPath]: value } } },
      });
    }
    return;
  }

  if (filter.operator === ArrayStringFilterOperator.ANY_OF) {
    // ANY_OF: At least one of the specified tags must be present
    compound.filter.push({
      nested: {
        path: "tags",
        query: { bool: { filter: [{ terms: { [fieldPath]: tagValues } }] } },
      },
    });
    return;
  }

  if (filter.operator === ArrayStringFilterOperator.NONE_OF) {
    // NONE_OF: None of the specified tags should be present
    // The must_not must be at the outer level, wrapping the nested query
    compound.must_not.push({
      nested: {
        path: "tags",
        query: { bool: { filter: [{ terms: { [fieldPath]: tagValues } }] } },
      },
    });
    return;
  }
}

interface SupportTicketOpensearchFilterHandlerInput {
  filter: AdvancedFilterData;
  path: SupportTicketFilterPath;
  compound: SupportTicketOpensearchBooleanQuery;
  options?: { timeZoneId?: string | null };
}

type SupportTicketOpensearchFilterHandler = (
  input: SupportTicketOpensearchFilterHandlerInput,
) => void;

function getKeywordPath(
  path: SupportTicketFilterPath,
): SupportTicketOpensearchPath {
  return path.keywordPath ?? path.opensearchPath;
}

const genericOpensearchFilterHandler: SupportTicketOpensearchFilterHandler = ({
  filter,
  path,
  compound,
  options,
}) => {
  getFilterBuilder(filter).addOpensearchFilters({
    filter,
    opensearchPath: path.opensearchPath,
    keywordPath: path.keywordPath,
    compound,
    timeZoneId: options?.timeZoneId ?? null,
  });
};

const wordsOpensearchFilterHandler: SupportTicketOpensearchFilterHandler = ({
  filter,
  compound,
}) => {
  if (!isArrayStringFilter(filter)) {
    return;
  }
  addWordsFilter(filter, compound);
};

const customerNameOpensearchFilterHandler: SupportTicketOpensearchFilterHandler =
  ({ filter, compound }) => {
    if (!isArrayStringFilter(filter)) {
      return;
    }
    addCustomerSearchFilter(filter, compound);
  };

const supportTicketTagsOpensearchFilterHandler: SupportTicketOpensearchFilterHandler =
  ({ filter, path, compound }) => {
    if (!isArrayStringFilter(filter)) {
      return;
    }
    addSupportTicketTagsFilter(filter, path, compound);
  };

const assigneesOpensearchFilterHandler: SupportTicketOpensearchFilterHandler =
  ({ filter, path, compound }) => {
    if (!isArrayStringFilter(filter)) {
      return;
    }

    const values = filter.value ?? [];
    if (values.length === 0) {
      return;
    }

    const sentinelValues = values.filter(
      (v) => v === UNASSIGNED_ASSIGNEE_SENTINEL,
    );
    const userValues = values.filter((v) => v !== UNASSIGNED_ASSIGNEE_SENTINEL);

    const hasSentinel = sentinelValues.length > 0;
    const hasUsers = userValues.length > 0;
    const keywordPath = getKeywordPath(path);

    if (filter.operator === ArrayStringFilterOperator.ANY_OF) {
      if (hasSentinel && hasUsers) {
        compound.filter.push({
          bool: {
            should: [
              {
                bool: {
                  must_not: [{ exists: { field: path.opensearchPath } }],
                },
              },
              { terms: { [keywordPath]: userValues } },
            ],
            minimum_should_match: 1,
          },
        });
      } else if (hasSentinel) {
        compound.must_not.push({ exists: { field: path.opensearchPath } });
      } else if (hasUsers) {
        compound.filter.push({ terms: { [keywordPath]: userValues } });
      }
      return;
    }

    if (filter.operator === ArrayStringFilterOperator.NONE_OF) {
      if (hasSentinel && hasUsers) {
        compound.must.push({ exists: { field: path.opensearchPath } });
        compound.must_not.push({ terms: { [keywordPath]: userValues } });
      } else if (hasSentinel) {
        compound.must.push({ exists: { field: path.opensearchPath } });
      } else if (hasUsers) {
        compound.must_not.push({ terms: { [keywordPath]: userValues } });
      }
      return;
    }
  };

function createNotFutureSnoozedClause(
  nowIso: string,
): SupportTicketOpensearchQuery {
  return {
    bool: {
      should: [
        { bool: { must_not: [{ exists: { field: "snoozedUntil" } }] } },
        { range: { snoozedUntil: { lte: nowIso } } },
      ],
      minimum_should_match: 1,
    },
  };
}

function createFutureSnoozedClause(
  nowIso: string,
): SupportTicketOpensearchQuery {
  return { range: { snoozedUntil: { gt: nowIso } } };
}

function createEffectiveStatusClause(
  status: SupportTicketStatus,
  nowIso: string,
): SupportTicketOpensearchQuery {
  switch (status) {
    case "OPEN":
      return {
        bool: {
          filter: [
            { term: { status: "OPEN" } },
            createNotFutureSnoozedClause(nowIso),
          ],
        },
      };
    case "SNOOZED":
      return {
        bool: {
          should: [
            { term: { status: "SNOOZED" } },
            {
              bool: {
                filter: [
                  { term: { status: "OPEN" } },
                  createFutureSnoozedClause(nowIso),
                ],
              },
            },
          ],
          minimum_should_match: 1,
        },
      };
    case "CLOSED":
    case "IN_PROGRESS":
    case "HIDDEN":
    case "ARCHIVED":
    case "DELETED":
    case "DRAFT":
      return { term: { status } };
  }
  const _exhaustive: never = status;
  return _exhaustive;
}

function createStatusClause(
  status: string,
  nowIso: string,
): SupportTicketOpensearchQuery {
  return isSupportTicketStatus(status)
    ? createEffectiveStatusClause(status, nowIso)
    : { match_none: {} };
}

const statusOpensearchFilterHandler: SupportTicketOpensearchFilterHandler = ({
  filter,
  compound,
}) => {
  if (!isArrayStringFilter(filter)) {
    return;
  }

  const values = filter.value ?? [];
  if (values.length === 0) {
    return;
  }

  const nowIso = new Date().toISOString();
  const statusClauses = values.map((status) =>
    createStatusClause(status, nowIso),
  );

  if (filter.operator === ArrayStringFilterOperator.ANY_OF) {
    if (statusClauses.length === 1) {
      const [statusClause] = statusClauses;
      if (statusClause) {
        compound.filter.push(statusClause);
      }
      return;
    }
    compound.filter.push({
      bool: { should: statusClauses, minimum_should_match: 1 },
    });
    return;
  }

  if (filter.operator === ArrayStringFilterOperator.NONE_OF) {
    compound.must_not.push({
      bool: { should: statusClauses, minimum_should_match: 1 },
    });
    return;
  }

  if (filter.operator === ArrayStringFilterOperator.ALL_OF) {
    compound.filter.push(...statusClauses);
  }
};

const supportTicketOpensearchFilterHandlers = {
  status: statusOpensearchFilterHandler,
  assignees: assigneesOpensearchFilterHandler,
  assigneeType: genericOpensearchFilterHandler,
  channels: genericOpensearchFilterHandler,
  supportTicketTags: supportTicketTagsOpensearchFilterHandler,
  customerTags: genericOpensearchFilterHandler,
  mentions: genericOpensearchFilterHandler,
  readAt: genericOpensearchFilterHandler,
  words: wordsOpensearchFilterHandler,
  createdAt: genericOpensearchFilterHandler,
  closedAt: genericOpensearchFilterHandler,
  lastResponseAt: genericOpensearchFilterHandler,
  lastCustomerMessageAt: genericOpensearchFilterHandler,
  lastMerchantMessageAt: genericOpensearchFilterHandler,
  customerName: customerNameOpensearchFilterHandler,
  participantKeys: genericOpensearchFilterHandler,
  primaryParticipantKeys: genericOpensearchFilterHandler,
  shopperParticipantIds: genericOpensearchFilterHandler,
  merchantUserParticipantIds: genericOpensearchFilterHandler,
  redoUserParticipantIds: genericOpensearchFilterHandler,
  agentParticipantIds: genericOpensearchFilterHandler,
  systemParticipantKeys: genericOpensearchFilterHandler,
  numDrafts: genericOpensearchFilterHandler,
  numSentMessages: genericOpensearchFilterHandler,
  numOutboundMessages: genericOpensearchFilterHandler,
  numCustomerMessages: genericOpensearchFilterHandler,
  numMerchantSentMessages: genericOpensearchFilterHandler,
  numAiSentMessages: genericOpensearchFilterHandler,
  numAutoReplySentMessages: genericOpensearchFilterHandler,
  numSystemSentMessages: genericOpensearchFilterHandler,
  numInternalNotes: genericOpensearchFilterHandler,
} satisfies Record<
  SupportTicketFilterName,
  SupportTicketOpensearchFilterHandler
>;

/**
 * Adds a single filter to an OpenSearch boolean query using the generic filter builder.
 */
export function addSupportTicketFilterToOpensearch(
  filter: AdvancedFilterData,
  compound: SupportTicketOpensearchBooleanQuery,
  options?: { timeZoneId?: string | null },
): void {
  const filterName = resolveSupportTicketFilterName(filter.name);
  if (!filterName) {
    return;
  }

  const path = getSupportTicketFilterPath(filterName);
  supportTicketOpensearchFilterHandlers[filterName]({
    filter,
    path,
    compound,
    options,
  });
}

/**
 * Processes a filter group recursively, adding filters to the compound query.
 * Handles nested AND/OR groups by creating appropriate nested bool queries.
 */
function processFilterGroup(
  group: FilterGroup,
  compound: SupportTicketOpensearchBooleanQuery,
  options?: { timeZoneId?: string | null },
  scopedLifecycleOptions?: ScopedLifecycleExclusionOptions,
): void {
  interface OpensearchWalkContext {
    compound: SupportTicketOpensearchBooleanQuery;
    inheritedStatuses: ReadonlySet<SupportTicketStatus>;
  }

  const processGroup = (
    currentGroup: FilterGroup,
    context: OpensearchWalkContext,
  ): void => {
    const groupStatuses =
      currentGroup.operator === FilterOperator.AND
        ? combineStatuses(
            context.inheritedStatuses,
            getConjunctiveExplicitStatuses(currentGroup),
          )
        : context.inheritedStatuses;
    const groupContext = { ...context, inheritedStatuses: groupStatuses };

    if (currentGroup.operator === FilterOperator.OR) {
      groupContext.compound.minimum_should_match =
        (groupContext.compound.minimum_should_match ?? 0) === 0
          ? 1
          : groupContext.compound.minimum_should_match;
    }

    for (const clause of currentGroup.clauses) {
      if (isFilterGroup(clause)) {
        const childCompound = createEmptyBoolQuery();
        processGroup(clause, {
          compound: childCompound,
          inheritedStatuses: groupContext.inheritedStatuses,
        });
        if (currentGroup.operator === FilterOperator.AND) {
          groupContext.compound.filter.push({ bool: childCompound });
        } else {
          if (clause.operator !== FilterOperator.OR && scopedLifecycleOptions) {
            addScopedLifecycleExclusions(
              childCompound,
              scopedLifecycleOptions,
              combineStatuses(
                groupContext.inheritedStatuses,
                getConjunctiveExplicitStatuses(clause),
              ),
            );
          }
          groupContext.compound.should.push({ bool: childCompound });
        }
        continue;
      }

      if (currentGroup.operator === FilterOperator.AND) {
        addSupportTicketFilterToOpensearch(
          clause,
          groupContext.compound,
          options,
        );
      } else {
        const clauseCompound = createEmptyBoolQuery();
        addSupportTicketFilterToOpensearch(clause, clauseCompound, options);
        if (scopedLifecycleOptions) {
          addScopedLifecycleExclusions(
            clauseCompound,
            scopedLifecycleOptions,
            combineStatuses(
              groupContext.inheritedStatuses,
              getFilterExplicitlyIncludedStatuses(clause),
            ),
          );
        }
        groupContext.compound.should.push({ bool: clauseCompound });
      }
    }
  };

  processGroup(group, { compound, inheritedStatuses: new Set() });
}

/**
 * Builds an OpenSearch boolean query from a filter group.
 * Supports nested AND/OR filter groups.
 * By default, excludes soft-deleted and AI-assigned support tickets.
 */
export function buildSupportTicketOpensearchQuery(
  accountId: AccountId,
  filterGroup: FilterGroup | null | undefined,
  options?: { timeZoneId?: string | null },
): SupportTicketOpensearchBooleanQuery {
  const compound = createEmptyBoolQuery();

  // Always filter by team and product kind.
  compound.filter.push({ term: { accountId } });
  // Keep support-ticket search isolated from AskRedo and merchant-support docs.
  compound.filter.push({ term: { kind: "shopper_support" } });

  const leafFilters = filterGroup
    ? getSupportTicketLeafFilters(filterGroup)
    : [];
  const includeSpamExclusion =
    !filterGroup || !filterGroupReferencesSpamTag(filterGroup);
  const includeAiAssigneeExclusion =
    !filterGroup || !filterGroupReferencesAiAssigneeType(filterGroup);
  const lifecycleExclusionOptions = getDefaultExclusionOptions(leafFilters);
  addDefaultSupportTicketExclusions(compound, {
    includeSpamExclusion,
    includeAiAssigneeExclusion,
    ...lifecycleExclusionOptions,
  });

  if (filterGroup) {
    processFilterGroup(
      filterGroup,
      compound,
      options,
      getScopedLifecycleExclusionOptions(lifecycleExclusionOptions),
    );
  }

  return compound;
}

/**
 * Builds an OpenSearch boolean query from a flat array of filters.
 * All filters are combined with AND logic.
 * By default, excludes soft-deleted and AI-assigned support tickets.
 */
export function buildSupportTicketOpensearchQueryFromFilters(
  accountId: AccountId,
  filters: AdvancedFilterData[],
  options?: { timeZoneId?: string | null },
): SupportTicketOpensearchBooleanQuery {
  const compound = createEmptyBoolQuery();

  // Always filter by accountId and product kind.
  compound.filter.push({ term: { accountId } });
  compound.filter.push({ term: { kind: "shopper_support" } });

  const includeSpamExclusion = !filters.some(filterReferencesSpamTag);
  const includeAiAssigneeExclusion = !filters.some(
    filterReferencesAiAssigneeType,
  );
  addDefaultSupportTicketExclusions(compound, {
    includeSpamExclusion,
    includeAiAssigneeExclusion,
    ...getDefaultExclusionOptions(filters),
  });

  for (const filter of filters) {
    addSupportTicketFilterToOpensearch(filter, compound, options);
  }

  return compound;
}
